import React, { memo, useCallback } from 'react';
import { useSnowballContext } from '../../contexts/snowball-context';

const ActionButtons = memo(({ onFetchAccounts, onRefetchAccounts, onCreateTransactions, isCreating = false }) => {
    const {
        hasAccounts,
        hasTransactions,
        hasExistingTransactions,
        canCalculate,
        canCreateTransactions,
        debitAccountID,
        accounts,
        isCalculating,
        masterStartDelayMonths,
        setMasterStartDelayMonths,
        calculateSchedules
    } = useSnowballContext();

    const handleFetchAccounts = useCallback(() => {
        if (onFetchAccounts) {
            onFetchAccounts();
        }
    }, [onFetchAccounts]);

    const handleRefetchAccounts = useCallback(() => {
        const confirmRefetch = window.confirm(
            "Are you sure you want to re-fetch your accounts? This will clear any adjustments you've made."
        );
        if (confirmRefetch && onRefetchAccounts) {
            onRefetchAccounts();
        }
    }, [onRefetchAccounts]);

    const handleCalculate = useCallback(() => {
        if (canCalculate) {
            calculateSchedules(accounts, true);
        }
    }, [canCalculate, accounts, calculateSchedules]);

    const handleCreateTransactions = useCallback(async () => {
        if (onCreateTransactions) {
            await onCreateTransactions();
        }
    }, [onCreateTransactions]);

    if (hasExistingTransactions) {
        return null;
    }

    return (
        <div style={{
            textAlign: 'center',
            margin: '2rem 0',
            padding: '2rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #ddd'
        }}>
            <div style={{ marginBottom: '1rem' }}>
                <h3 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>
                    Snowball Actions
                </h3>
                <p style={{ margin: '0', color: '#666', fontSize: '0.9rem' }}>
                    {!hasAccounts && "Start by fetching your debt accounts"}
                    {hasAccounts && !debitAccountID && "Select a payment account to continue"}
                    {hasAccounts && debitAccountID && !canCalculate && "Configure your payment preferences"}
                    {canCalculate && "Calculate payment schedules to see your snowball plan"}
                </p>
            </div>

            {/* Master Start Delay Field */}
            {hasAccounts && debitAccountID && (
                <div style={{
                    marginBottom: '2rem',
                    padding: '1rem',
                    backgroundColor: '#fff',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '1rem',
                        flexWrap: 'wrap'
                    }}>
                        <label style={{
                            fontWeight: '500',
                            color: '#333',
                            fontSize: '0.9rem'
                        }}>
                            Master Start Delay:
                        </label>
                        <input
                            type="number"
                            min="0"
                            max="48"
                            value={masterStartDelayMonths}
                            onChange={(e) => setMasterStartDelayMonths(parseInt(e.target.value) || 0)}
                            disabled={isCalculating || isCreating}
                            style={{
                                width: '80px',
                                padding: '0.5rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '0.9rem',
                                textAlign: 'center'
                            }}
                        />
                        <span style={{
                            fontSize: '0.9rem',
                            color: '#666'
                        }}>
                            months
                        </span>
                        <div style={{
                            fontSize: '0.8rem',
                            color: '#666',
                            fontStyle: 'italic',
                            maxWidth: '300px',
                            textAlign: 'center'
                        }}>
                            {masterStartDelayMonths === 0 ? 
                                'All accounts start immediately' : 
                                `All accounts start ${masterStartDelayMonths} month${masterStartDelayMonths === 1 ? '' : 's'} later (unless overridden)`
                            }
                        </div>
                    </div>
                </div>
            )}

            <div style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'center',
                flexWrap: 'wrap'
            }}>
                {/* Account Management Buttons */}
                {!hasAccounts ? (
                    <button
                        className="button button-green button-lg"
                        onClick={handleFetchAccounts}
                        disabled={isCreating}
                        style={{ minWidth: '160px', opacity: isCreating ? 0.6 : 1 }}
                    >
                        {isCreating ? '⏳ Please Wait...' : '📊 Fetch Accounts'}
                    </button>
                ) : (
                    <button
                        className="button button-red"
                        onClick={handleRefetchAccounts}
                        disabled={isCreating}
                        style={{ minWidth: '160px', opacity: isCreating ? 0.6 : 1 }}
                    >
                        {isCreating ? '⏳ Wait...' : '🔄 Re-fetch Accounts'}
                    </button>
                )}

                {/* Calculate Button */}
                {debitAccountID && hasAccounts && (
                    <button
                        className={`button ${canCalculate ? 'button-blue' : 'button-gray'} ${canCalculate ? 'button-lg' : ''}`}
                        onClick={handleCalculate}
                        disabled={!canCalculate || isCalculating || isCreating}
                        style={{ minWidth: '160px', opacity: (isCalculating || isCreating) ? 0.6 : 1 }}
                    >
                        {isCalculating ? (
                            <>⏳ Calculating...</>
                        ) : isCreating ? (
                            <>⏳ Please Wait...</>
                        ) : (
                            <>🧮 Calculate Snowball</>
                        )}
                    </button>
                )}
            </div>

            {/* Create Transactions Button */}
            {canCreateTransactions && (
                <div style={{ 
                    marginTop: '2rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid #ddd'
                }}>
                    <div style={{ marginBottom: '1rem' }}>
                        <h4 style={{ margin: '0 0 0.5rem 0', color: isCreating ? '#ffc107' : '#28a745' }}>
                            {isCreating ? '⏳ Creating Transactions...' : '✅ Ready to Create Transactions'}
                        </h4>
                        <p style={{ margin: '0', color: '#666', fontSize: '0.9rem' }}>
                            {isCreating 
                                ? 'Please wait while we create your debt snowball transactions. This may take a moment.'
                                : 'Your snowball plan is calculated and ready. Create transactions to automate your debt payments.'
                            }
                        </p>
                    </div>
                    
                    <button
                        className={`button ${isCreating ? 'button-stroke-green' : 'button-green'}`}
                        onClick={handleCreateTransactions}
                        disabled={isCreating}
                        style={{ 
                            minWidth: '200px',
                            opacity: isCreating ? 0.8 : 1,
                            cursor: isCreating ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {isCreating ? '⏳ Creating...' : '💰 Create Transactions'}
                    </button>
                </div>
            )}

            {/* Progress Indicator */}
            {hasAccounts && debitAccountID && (
                <div style={{
                    marginTop: '1rem',
                    fontSize: '0.8rem',
                    color: '#666'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        flexWrap: 'wrap'
                    }}>
                        <span style={{ color: hasAccounts ? '#28a745' : '#ccc' }}>
                            ✓ Accounts
                        </span>
                        <span>→</span>
                        <span style={{ color: debitAccountID ? '#28a745' : '#ccc' }}>
                            ✓ Payment Account
                        </span>
                        <span>→</span>
                        <span style={{ color: canCalculate ? '#28a745' : '#ccc' }}>
                            {canCalculate ? '✓' : '○'} Configure
                        </span>
                        <span>→</span>
                        <span style={{ color: hasTransactions ? '#28a745' : '#ccc' }}>
                            {hasTransactions ? '✓' : '○'} Calculate
                        </span>
                        <span>→</span>
                        <span style={{ color: canCreateTransactions ? '#28a745' : '#ccc' }}>
                            {canCreateTransactions ? '✓' : '○'} Create
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
});

ActionButtons.displayName = 'ActionButtons';

export default ActionButtons;